import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { 
  getMarketData, 
  getHistoricRSI, 
  getRegimeInfo
} from '../services/marketService';
import { calculateSID } from '../services/dynamicStressService';
import { API_BASE_URL } from '../utils/constants';
import { translateError } from '../utils/errorTranslations';
import { useAuth } from './AuthContext';

// Criar o contexto de mercado
const MarketContext = createContext();

/**
 * Provider de mercado para gerenciar os dados de mercado
 */
export const MarketProvider = ({ children }) => {
  const [bitcoinPrice, setBitcoinPrice] = useState(null);
  const [priceChange24h, setPriceChange24h] = useState(null);
  const [rsi, setRsi] = useState(null);
  const [rsiDirectional, setRsiDirectional] = useState(null);
  const [regime, setRegime] = useState(null);
  const [historicRSI, setHistoricRSI] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // ✅ NEW: Estados para Dynamic Stress Index (SID)
  const [sidData, setSidData] = useState(null);
  const [sidLoading, setSidLoading] = useState(false);
  const [sidError, setSidError] = useState(null);
  
  // ✅ NEW: Estados para Divergence System
  const [divergenceData, setDivergenceData] = useState(null);
  const [divergenceLoading, setDivergenceLoading] = useState(false);
  const [divergenceError, setDivergenceError] = useState(null);
  
  // Sistema sempre pronto (sem verificação de readiness)
  const [systemReady] = useState(true);
  
  // 🔥 USAR AuthContext para verificar autenticação
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  
  // Função para carregar dados de mercado
  const loadMarketData = useCallback(async (forceRefresh = false, isInitialLoad = false) => {
    // ✅ SÓ mostrar loading se for carregamento inicial ou refresh forçado
    if (isInitialLoad || forceRefresh) {
      setIsLoading(true);
    }
    setError(null);
    
    try {
      // ✅ Sistema sempre pronto - sem verificação de readiness
      // ✅ Log diferenciado para updates automáticos vs. carregamentos iniciais
      if (isInitialLoad) {
        console.log("🔄 Carregando dados de mercado (inicial)...");
      } else if (forceRefresh) {
        console.log("🔄 Carregando dados de mercado (refresh forçado)...");
      } else {
        console.log("🔄 Atualizando dados de mercado (automático/silencioso)...");
      }
      
      // Obter dados atuais do mercado
      // ✅ RESILIÊNCIA: se force_refresh falhar, tentar novamente sem force_refresh (cache/último valor válido)
      let marketData;
      try {
        marketData = await getMarketData(forceRefresh);
      } catch (primaryErr) {
        if (forceRefresh) {
          console.warn(
            "⚠️ Falha no refresh forçado de market-data; tentando sem force_refresh...",
            primaryErr
          );
          marketData = await getMarketData(false);
        } else {
          throw primaryErr;
        }
      }
      console.log("✅ Dados de mercado recebidos:", marketData);
      
      // Atualizar estado com dados recebidos
      setBitcoinPrice(marketData.price);
      setPriceChange24h(marketData.change_24h);
      setRsi(marketData.rsi);
      // Direcional global do backend (se disponível)
      setRsiDirectional({
        rsi_prev: marketData.rsi_prev,
        rsi_delta: marketData.rsi_delta,
        rsi_direction: marketData.rsi_direction,
        rsi_zone_key: marketData.rsi_zone_key,
        rsi_directional_key: marketData.rsi_directional_key,
      });
      setRegime(marketData.regime);
      setLastUpdated(new Date());
      
      // ✅ NEW: Auto-calcular SID com ROC Enhancement quando temos RSI, regime e preço válidos
      if (marketData.rsi && marketData.regime) {
        await loadSIDData(marketData.rsi, marketData.regime, marketData.price, isInitialLoad || forceRefresh);
      }
      
      // ✅ NEW: Carregar dados de Divergência
      await loadDivergenceData(isInitialLoad || forceRefresh);
      
      // ✅ Só carregar dados históricos em carregamentos iniciais ou refresh forçado
      // Updates automáticos não precisam recarregar dados históricos
      if (isInitialLoad || forceRefresh) {
      // Obter dados históricos (até 180 dias = aproximadamente 6 meses)
        console.log("🔄 Carregando dados históricos de RSI dos últimos 6 meses...");
      const historicData = await getHistoricRSI(180, forceRefresh);
        console.log("✅ Dados históricos recebidos:", historicData?.length);
      
      // Log detalhado para verificar a estrutura dos dados
      if (Array.isArray(historicData) && historicData.length > 0) {
        console.log("Amostra dos primeiros 5 registros:", historicData.slice(0, 5));
        console.log("Amostra dos últimos 5 registros:", historicData.slice(-5));
        
        // Verificar valores extremos para identificar problemas
        const rsiValues = historicData.map(item => item.rsi).filter(val => val !== undefined && val !== null);
        const priceValues = historicData.map(item => item.price).filter(val => val !== undefined && val !== null);
        
        if (rsiValues.length > 0) {
          const minRsi = Math.min(...rsiValues);
          const maxRsi = Math.max(...rsiValues);
          console.log(`RSI - min: ${minRsi}, max: ${maxRsi}`);
          
          // Verificar se os valores de RSI estão dentro do intervalo esperado (0-100)
          if (minRsi < 0 || maxRsi > 100) {
            console.warn(`Valores de RSI fora do intervalo esperado (0-100): min=${minRsi}, max=${maxRsi}`);
          }
        }
        
        if (priceValues.length > 0) {
          const minPrice = Math.min(...priceValues);
          const maxPrice = Math.max(...priceValues);
          console.log(`Preço - min: ${minPrice}, max: ${maxPrice}`);
        }
        
        // Filtrar para garantir que temos apenas dados dos últimos 6 meses
        const filteredData = historicData
          .filter(item => {
            // Validação adicional de dados
            if (!item || !item.date || item.rsi === undefined || item.rsi === null || item.price === undefined || item.price === null) {
              return false;
            }
            
            return true; // Usar todos os dados válidos, sem filtrar por data
          })
          .sort((a, b) => new Date(a.date) - new Date(b.date));
        
        // Pegar apenas os dados mais recentes (últimos 180 dias do dataset)
        const recentData = filteredData.slice(Math.max(0, filteredData.length - 180));
        
        console.log(`Dados históricos (últimos 180 dias do dataset): ${recentData.length} registros`);
        
        // Se os dados parecem corretos, usar. Caso contrário, usar dados simulados
        if (recentData.length > 0) {
          setHistoricRSI(recentData);
        } else {
          console.warn("Dados filtrados insuficientes, usando simulação");
          const demoData = generateDemoData(180, marketData.price, marketData.rsi);
          setHistoricRSI(demoData);
        }
      } else {
        console.warn("Dados históricos estão vazios ou em formato inválido:", historicData);
        
        // Somente em desenvolvimento, criar dados simulados
        if (process.env.NODE_ENV === 'development') {
          const demoData = generateDemoData(180, marketData.price, marketData.rsi);
          console.log("Usando dados simulados para desenvolvimento:", demoData.length, "registros");
          setHistoricRSI(demoData);
        } else {
          setHistoricRSI([]);
        }
        }
      } else {
        console.log("⏩ Pulando dados históricos para update automático/silencioso");
      }
      
      return marketData;
    } catch (err) {
      console.error('Error loading market data:', err);
      
      // Tratar erro normalmente
      setError(err.message || 'Erro ao carregar dados de mercado');
      
      // Em caso de erro, usar dados simulados em desenvolvimento (só em carregamentos iniciais)
      if ((isInitialLoad || forceRefresh) && process.env.NODE_ENV === 'development') {
        const demoData = generateDemoData(180);
        console.log("Usando dados simulados devido a erro:", demoData.length, "registros");
        setHistoricRSI(demoData);
      }
      
      // NÃO re-lançar: quem chama (useEffect) não trata a rejeição,
      // causando unhandled promise rejection e potencial crash.
      // O estado `error` já foi setado acima para os consumidores reagirem.
    } finally {
      // ✅ SÓ remover loading se foi definido anteriormente
      if (isInitialLoad || forceRefresh) {
        setIsLoading(false);
      }
    }
  }, []);

  // ✅ NEW: Função para carregar dados SID com ROC Enhancement
  const loadSIDData = useCallback(async (currentRsi, currentRegime, currentPrice = null, showLoading = false) => {
    if (!currentRsi || !currentRegime) {
      setSidData(null);
      setSidError(null);
      return;
    }

    if (showLoading) {
      setSidLoading(true);
    }
    setSidError(null);

    try {
      // Log diferenciado baseado na disponibilidade do preço para ROC Enhancement
      if (currentPrice && currentPrice > 0) {
        console.log("🔄 Carregando dados SID Enhanced:", { rsi: currentRsi, regime: currentRegime, price: currentPrice });
      } else {
        console.log("🔄 Carregando dados SID (base):", { rsi: currentRsi, regime: currentRegime });
      }
      
      const sidResult = await calculateSID(currentRsi, currentRegime, currentPrice);
      console.log("✅ SID calculado com sucesso:", sidResult);
      
      setSidData(sidResult);
    } catch (err) {
      console.error('Error loading SID data:', err);
      
      // ✅ CORREÇÃO: Não mostrar erro se for cancelamento de requisição
      if (err.name === 'AxiosError' && err.code === 'ECONNABORTED') {
        console.log('SID request was aborted (likely due to page reload), not showing error to user');
        setSidError(null);
      } else {
        setSidError(err.message || translateError('Erro ao calcular índice de estresse'));
      }
      setSidData(null);
    } finally {
      if (showLoading) {
        setSidLoading(false);
      }
    }
  }, []);

  // ✅ NEW: Função para carregar dados de Divergência
  const loadDivergenceData = useCallback(async (showLoading = false) => {
    if (showLoading) {
      setDivergenceLoading(true);
    }
    setDivergenceError(null);

    try {
      console.log("🔄 Carregando dados de Divergência...");
      
      // Fazer requisição para o endpoint de divergência
      const response = await fetch(`${API_BASE_URL}/divergence-status/`);
      
      if (!response.ok) {
        throw new Error(`Erro ao carregar divergência: ${response.statusText}`);
      }
      
      const divergenceResult = await response.json();
      console.log("✅ Divergência carregada com sucesso:", divergenceResult);
      
      setDivergenceData(divergenceResult);
    } catch (err) {
      console.error('Error loading Divergence data:', err);
      
      // Não mostrar erro se for cancelamento de requisição
      if (err.name === 'AxiosError' && err.code === 'ECONNABORTED') {
        console.log('Divergence request was aborted (likely due to page reload), not showing error to user');
        setDivergenceError(null);
      } else {
        setDivergenceError(err.message || 'Erro ao carregar dados de divergência');
      }
      setDivergenceData(null);
    } finally {
      if (showLoading) {
        setDivergenceLoading(false);
      }
    }
  }, []);
  
  // Gerar dados simulados para desenvolvimento/testes
  const generateDemoData = (days = 180, currentPrice = 50000, currentRsi = 50) => {
    const demoData = [];
    const today = new Date();
    
    // Padrão cíclico para simular um mercado realista
    for (let i = days; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      
      // Preço simulado com tendência e ciclos
      const cycleFactor = Math.sin(i / 30) * 0.15; // Ciclo de 30 dias
      const trendFactor = i / days * 0.3; // Tendência de alta ao longo do período
      const randomFactor = (Math.random() - 0.5) * 0.05; // Ruído aleatório
      
      const priceFactor = 1 + cycleFactor - trendFactor + randomFactor;
      const price = currentPrice * priceFactor;
      
      // RSI simulado (inversamente correlacionado com o preço, com atraso)
      const delay = 5;
      const delayedI = Math.max(0, i - delay);
      const rsiCycleFactor = Math.sin(delayedI / 30) * 25;
      const rsiRandomFactor = (Math.random() - 0.5) * 5;
      const rsi = 50 + rsiCycleFactor + rsiRandomFactor;
      
      demoData.push({
        date: date.toISOString().split('T')[0],
        price,
        rsi: Math.min(Math.max(rsi, 0), 100) // Limitar entre 0 e 100
      });
    }
    
    return demoData;
  };
  
  // Função para obter o regime atual
  const getCurrentRegime = useCallback(async () => {
    try {
      console.log("Obtendo informações do regime atual...");
      const regimeData = await getRegimeInfo();
      console.log("Informações do regime recebidas:", regimeData);
      setRegime(regimeData.current_regime);
      return regimeData;
    } catch (err) {
      console.error('Error getting current regime:', err);
      setError(err.message || 'Erro ao obter regime atual');
      throw err;
    }
  }, []);
  
  // Função para obter dados do buffer de 180 dias (já carregado em memória)
  const getBufferData = useCallback(async () => {
    try {
      console.log("=== INICIANDO getBufferData ===");
      console.log("API_BASE_URL:", API_BASE_URL);
      console.log("URL completa:", `${API_BASE_URL}/rsi/buffer/`);
      
      const response = await fetch(`${API_BASE_URL}/rsi/buffer/`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });
      
      console.log("=== RESPOSTA RECEBIDA ===");
      console.log("Status:", response.status);
      console.log("Status Text:", response.statusText);
      console.log("Headers:", response.headers);
      
      if (!response.ok) {
        throw new Error(`Erro ao buscar buffer: ${response.status} - ${response.statusText}`);
      }
      
      const bufferData = await response.json();
      console.log("=== DADOS DO BUFFER PARSEADOS ===");
      console.log("Tipo dos dados:", typeof bufferData);
      console.log("É array?", Array.isArray(bufferData));
      console.log("Quantidade de registros:", bufferData?.length || 0);
      
      if (bufferData && bufferData.length > 0) {
        console.log("Primeiro registro:", bufferData[0]);
        console.log("Último registro:", bufferData[bufferData.length - 1]);
        console.log("Estrutura do primeiro registro:", Object.keys(bufferData[0]));
      }
      
      return bufferData || [];
      
    } catch (error) {
      console.error("=== ERRO EM getBufferData ===");
      console.error("Erro:", error);
      console.error("Stack:", error.stack);
      return [];
    }
  }, []);
  

  // ✅ OTIMIZADO: Carregamento inicial único quando usuário autentica
  useEffect(() => {
    console.log("🚀 MarketContext useEffect - Status:", { isAuthenticated, authLoading });
    
    if (authLoading) {
      console.log("⏳ Aguardando verificação de autenticação...");
      setIsLoading(true);
      return;
    }
    
    if (!isAuthenticated) {
      console.log("⚠️ Usuário não autenticado - MarketContext em standby");
      setIsLoading(false);
      setError(null);
      return;
    }
    
    // Usuário autenticado - carregar dados iniciais APENAS UMA VEZ
    // forceRefresh na carga inicial com fallback automático para cache se falhar
    console.log("✅ Usuário autenticado - Carregando dados de mercado (inicial)...");
    loadMarketData(true, true).catch(() => {
      // Erro já tratado dentro de loadMarketData (setError); catch apenas evita unhandled rejection
    });
    
    // Atualizar RSI, regime e dados complementares a cada 2 minutos
    // Usa cache normal — threads do backend já atualizam a cada 30s
    const intervalId = setInterval(() => {
      console.log("⏰ Atualizando RSI, regime e dados complementares (2min)...");
      loadMarketData(false, false).catch(() => {});
    }, 2 * 60 * 1000); // 2 minutos
    
    return () => {
      console.log("🛑 Limpando intervalo de atualização do MarketContext");
      clearInterval(intervalId);
    };
  }, [isAuthenticated, authLoading, loadMarketData]);
  
  // Valor do contexto
  const value = {
    bitcoinPrice,
    priceChange24h,
    rsi,
    rsiDirectional,
    regime,
    historicRSI,
    lastUpdated,
    isLoading,
    error,
    loadMarketData,
    getCurrentRegime,
    getBufferData,
    // ✅ NEW: Dados do SID
    sidData,
    sidLoading,
    sidError,
    loadSIDData,
    // ✅ NEW: Dados de Divergência
    divergenceData,
    divergenceLoading,
    divergenceError,
    loadDivergenceData,
    // 🔥 NEW: System Readiness
    systemReady,
  };
  
  return (
    <MarketContext.Provider value={value}>
      {children}
    </MarketContext.Provider>
  );
};

/**
 * Hook para usar o contexto de mercado
 */
export const useMarket = () => {
  const context = useContext(MarketContext);
  
  if (!context) {
    throw new Error('useMarket must be used within a MarketProvider');
  }
  
  return context;
};

export default MarketContext; 